import { logbookClassFor } from '@/features/aircraft/classes';
import { nearestAerodrome } from '@/lib/aerodromes';
import type {
  AerodromeRef,
  Aircraft,
  LogbookClass,
  DerivedStats,
  LogbookEntry,
  LogbookLandings,
  LogbookTakeoffs,
  Track,
  TrackPoints,
} from '@/lib/db';

import { computeDerivedStats } from './derive-stats';
import { roundDecimalHours } from './time';

/**
 * What a track says about a flight, in logbook terms — computed once, in one
 * place.
 *
 * This used to live inside `draft.ts`, which was fine while a track only ever
 * became a *new* draft entry. The API matching flow (`api-match.ts`) needs the
 * same numbers for a different purpose: not to fill a blank row, but to diff
 * against a row a pilot has already typed. Two copies of "what does the track
 * think the night hours were" would be two answers the day one of them
 * changed, so there is one.
 *
 * Nothing here is authoritative. `PLAN.md` §2: the track is evidence, not
 * truth. `derivedFields` names exactly which of these the app actually had
 * something to say about, and it is those and only those that the grid
 * underlines and the match flow offers.
 */

export interface TrackDerivedValues {
  dayHr: number;
  nightHr: number;
  /** Which per-class hour column this entry's time lands under. Only known when the aircraft is on file. */
  engineClass?: LogbookClass;
  landings: LogbookLandings;
  takeoffs: LogbookTakeoffs;
  registration?: string;
  aircraftType?: string;
  /** Aerodrome codes, as the logbook's own `from`/`to` cells hold them. */
  from?: string;
  to?: string;
  /** The full references, for `Flight.departure`/`arrival`. */
  departure?: AerodromeRef;
  arrival?: AerodromeRef;
  derived: DerivedStats;
  /** Field ids (matching `LogbookColumn.id`) this track actually filled in. */
  derivedFields: string[];
}

/** Make/model if the record carries them, otherwise the ICAO type designator. */
export function aircraftTypeLabel(aircraft: Aircraft | undefined): string | undefined {
  if (aircraft === undefined) {
    return undefined;
  }
  const typeLine = [aircraft.make, aircraft.model].filter((part) => part !== undefined).join(' ');
  return typeLine !== '' ? typeLine : aircraft.icaoType;
}

/** The first fix that is actually a position — a leading `NaN` is a gap, not a departure point. */
function finiteFix(points: TrackPoints, fromEnd = false): { lat: number; lon: number } | undefined {
  const count = points.lat.length;
  for (let step = 0; step < count; step += 1) {
    const i = fromEnd ? count - 1 - step : step;
    const lat = points.lat[i];
    const lon = points.lon[i];
    if (lat !== undefined && lon !== undefined && Number.isFinite(lat) && Number.isFinite(lon)) {
      return { lat, lon };
    }
  }
  return undefined;
}

/** The aerodrome a fix sits at, keeping its own published position rather than the fix that found it. */
function aerodromeAt(fix: { lat: number; lon: number } | undefined): AerodromeRef | undefined {
  if (fix === undefined) {
    return undefined;
  }
  const match = nearestAerodrome(fix.lat, fix.lon);
  if (match === undefined) {
    return undefined;
  }
  const { icao, name, lat, lon } = match.aerodrome;
  return {
    ...(icao === undefined ? {} : { icao }),
    ...(name === undefined ? {} : { name }),
    ...(lat === undefined ? {} : { lat }),
    ...(lon === undefined ? {} : { lon }),
  };
}

export function trackDerivedValues(
  track: Track,
  points: TrackPoints,
  aircraft?: Aircraft,
): TrackDerivedValues {
  const derived = computeDerivedStats(track, points);
  const departure = aerodromeAt(finiteFix(points));
  const arrival = aerodromeAt(finiteFix(points, true));

  const totalHr = derived.airTimeMin === undefined ? 0 : derived.airTimeMin / 60;
  const nightHr = roundDecimalHours(Math.min(totalHr, (derived.nightMin ?? 0) / 60));
  const dayHr = roundDecimalHours(Math.max(totalHr - (derived.nightMin ?? 0) / 60, 0));

  const nightLandings = derived.nightLandingCount ?? 0;
  const landings: LogbookLandings = {
    day: Math.max(0, (derived.landingCount ?? 0) - nightLandings),
    night: nightLandings,
    touchAndGo: derived.touchAndGoCount ?? 0,
  };
  const nightTakeoffs = derived.nightTakeoffCount ?? 0;
  const takeoffs: LogbookTakeoffs = {
    day: Math.max(0, (derived.takeoffCount ?? 0) - nightTakeoffs),
    night: nightTakeoffs,
  };

  const aircraftType = aircraftTypeLabel(aircraft);
  const from = departure?.icao ?? departure?.name;
  const to = arrival?.icao ?? arrival?.name;

  /*
   * Only fields the track had something to say about. A zero is not "the track
   * says zero" — for a night landing count on a daytime flight it is simply
   * the absence of one — so a zero never gets tagged as derived and never
   * shows up as something to review.
   */
  const derivedFields: string[] = [];
  if (dayHr > 0) {
    derivedFields.push('time.dayHr');
  }
  if (nightHr > 0) {
    derivedFields.push('time.nightHr');
  }
  if (dayHr > 0 || nightHr > 0) {
    derivedFields.push('time.picHr');
  }
  if (landings.day > 0) {
    derivedFields.push('landings.day');
  }
  if (landings.night > 0) {
    derivedFields.push('landings.night');
  }
  if (landings.touchAndGo > 0) {
    derivedFields.push('landings.touchAndGo');
  }
  if (takeoffs.day > 0) {
    derivedFields.push('takeoffs.day');
  }
  if (takeoffs.night > 0) {
    derivedFields.push('takeoffs.night');
  }
  if (track.registration !== undefined) {
    derivedFields.push('registration');
  }
  if (aircraftType !== undefined) {
    derivedFields.push('aircraftType');
  }
  /*
   * The class decides which of the NZ-style per-class hour columns this
   * entry's time shows under (`classHoursColumn` in `columns.ts`), and it is
   * the aircraft record that knows it — so it is only the track's to propose
   * when there is an aircraft behind it.
   */
  if (aircraft !== undefined) {
    derivedFields.push('time.engineClass');
  }
  if (from !== undefined) {
    derivedFields.push('from');
  }
  if (to !== undefined) {
    derivedFields.push('to');
  }

  return {
    dayHr,
    nightHr,
    ...(aircraft === undefined ? {} : { engineClass: logbookClassFor(aircraft) }),
    landings,
    takeoffs,
    ...(track.registration === undefined ? {} : { registration: track.registration }),
    ...(aircraftType === undefined ? {} : { aircraftType }),
    ...(from === undefined ? {} : { from }),
    ...(to === undefined ? {} : { to }),
    ...(departure === undefined ? {} : { departure }),
    ...(arrival === undefined ? {} : { arrival }),
    derived,
    derivedFields,
  };
}

/**
 * The entry as the track would have written it — every derived field applied,
 * nothing else touched.
 *
 * Not for saving. It exists so the match flow can render "track says X, you
 * logged Y" by reading the *same* `LOGBOOK_COLUMNS` cells from both entries,
 * rather than formatting a second set of numbers its own way and drifting from
 * what the grid shows.
 */
export function applyTrackValues(entry: LogbookEntry, values: TrackDerivedValues): LogbookEntry {
  return {
    ...entry,
    ...(values.registration === undefined ? {} : { registration: values.registration }),
    ...(values.aircraftType === undefined ? {} : { aircraftType: values.aircraftType }),
    ...(values.from === undefined ? {} : { from: values.from }),
    ...(values.to === undefined ? {} : { to: values.to }),
    time: {
      ...entry.time,
      ...(values.engineClass === undefined ? {} : { engineClass: values.engineClass }),
      dayHr: values.dayHr,
      nightHr: values.nightHr,
      picHr: roundDecimalHours(values.dayHr + values.nightHr),
    },
    landings: { ...values.landings },
    takeoffs: { ...values.takeoffs },
  };
}
